import { createId } from '../utils/id';
import { occursOn, parseRecurrence } from '../utils/recurrence';
import { foldText } from '../utils/text';

const TASK_COLORS = new Set([
  'gray',
  'red',
  'orange',
  'yellow',
  'green',
  'teal',
  'blue',
  'indigo',
  'purple',
  'pink',
]);

export function taskColor(value) {
  return TASK_COLORS.has(value) ? value : '';
}

export function createTask(text, done = false) {
  return {
    id: createId(),
    text,
    done,
    notes: '',
    color: '',
    createdAt: Date.now(),
  };
}

export function listFrom(state, loc) {
  if (!loc) return [];
  if (loc.zone === 'day') return state.days[loc.date] || [];
  const page = state.pages.find((item) => item.id === loc.pageId);
  const list = page?.lists.find((item) => item.id === loc.listId);
  return list?.tasks || [];
}

export function withList(state, loc, tasks) {
  if (loc.zone === 'day') {
    const days = { ...state.days };
    if (tasks.length) days[loc.date] = tasks;
    else delete days[loc.date];
    return { ...state, days };
  }

  return {
    ...state,
    pages: state.pages.map((page) => {
      if (page.id !== loc.pageId) return page;
      return {
        ...page,
        lists: page.lists.map((list) =>
          list.id === loc.listId ? { ...list, tasks } : list
        ),
      };
    }),
  };
}

function seriesOf(state, id) {
  return (state.recurring || []).find((item) => item.id === id) || null;
}

function withSeries(state, id, patch) {
  return {
    ...state,
    recurring: state.recurring.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  };
}

export function addTask(state, loc, text) {
  if (loc?.zone === 'day') {
    const parsed = parseRecurrence(text);
    if (parsed && loc.date) {
      const item = {
        id: createId(),
        text: parsed.text,
        notes: '',
        every: parsed.every,
        unit: parsed.unit,
        start: loc.date,
        doneOn: [],
        createdAt: Date.now(),
      };
      return {
        state: { ...state, recurring: [...(state.recurring || []), item] },
        task: item,
        index: (state.recurring || []).length,
      };
    }
  }
  const tasks = listFrom(state, loc).slice();
  const task = createTask(text);
  tasks.push(task);
  return { state: withList(state, loc, tasks), task, index: tasks.length - 1 };
}

export function addTasks(state, loc, lines) {
  return { state: lines.reduce((current, line) => addTask(current, loc, line).state, state) };
}

export function saveRecurring(state, item) {
  const text = String(item?.text || '').trim();
  if (!text || !item?.start) return state;
  const every = Math.min(99, Math.max(1, Math.round(Number(item.every) || 1)));
  const unit = ['day', 'week', 'month', 'year'].includes(item.unit) ? item.unit : 'day';
  const existing = item.id ? seriesOf(state, item.id) : null;
  const next = {
    id: existing?.id || item.id || createId(),
    text,
    notes: existing?.notes || '',
    color: taskColor(existing?.color),
    every,
    unit,
    start: item.start,
    doneOn: existing?.doneOn || [],
    createdAt: existing?.createdAt || Date.now(),
  };
  const recurring = existing
    ? state.recurring.map((entry) => (entry.id === next.id ? next : entry))
    : [...(state.recurring || []), next];
  return { ...state, recurring };
}

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

// TeuxDeux rolls every unfinished day task onto today so open work stays in
// front of you. Finished tasks stay on the day they were completed. Future
// days and someday lists are left alone. Older days come first, then today.
export function rollUnfinished(state, todayKey) {
  if (!state || !DAY_KEY.test(todayKey) || !state.days || typeof state.days !== 'object') {
    return state;
  }

  const past = Object.keys(state.days)
    .filter((key) => DAY_KEY.test(key) && key < todayKey)
    .sort();
  if (!past.length) return state;

  const moved = [];
  const days = { ...state.days };
  let changed = false;

  past.forEach((key) => {
    const tasks = Array.isArray(days[key]) ? days[key] : [];
    const stay = [];
    tasks.forEach((task) => {
      if (task && typeof task === 'object' && !task.done) moved.push(task);
      else stay.push(task);
    });
    if (stay.length === tasks.length) return;
    changed = true;
    if (stay.length) days[key] = stay;
    else delete days[key];
  });

  if (!changed) return state;
  const today = Array.isArray(days[todayKey]) ? days[todayKey] : [];
  days[todayKey] = [...moved, ...today];
  return { ...state, days };
}

export function tasksForDay(state, dateKey) {
  const manual = state.days[dateKey] || [];
  const extra = (state.recurring || [])
    .filter((item) => occursOn(item, dateKey))
    .map((item) => ({
      id: item.id,
      text: item.text,
      done: (item.doneOn || []).includes(dateKey),
      notes: item.notes || '',
      color: taskColor(item.color),
      createdAt: item.createdAt,
      recurring: true,
      every: item.every,
      unit: item.unit,
    }));
  return [...manual, ...extra];
}

export function updateTaskText(state, loc, id, text) {
  const series = seriesOf(state, id);
  if (series && loc?.zone === 'day') {
    const next = text.trim();
    if (!next) return { ...state, recurring: state.recurring.filter((item) => item.id !== id) };
    if (next === series.text) return state;
    return withSeries(state, id, { text: next });
  }
  const current = listFrom(state, loc);
  const existing = current.find((task) => task.id === id);
  if (!existing || existing.text === text) return state;
  return withList(
    state,
    loc,
    current.map((task) => (task.id === id ? { ...task, text } : task))
  );
}

export function setTaskColor(state, loc, id, color) {
  const next = taskColor(color);
  const series = seriesOf(state, id);
  if (series && loc?.zone === 'day') {
    if (taskColor(series.color) === next) return state;
    return withSeries(state, id, { color: next });
  }
  const current = listFrom(state, loc);
  const existing = current.find((task) => task.id === id);
  if (!existing || taskColor(existing.color) === next) return state;
  return withList(
    state,
    loc,
    current.map((task) => (task.id === id ? { ...task, color: next } : task))
  );
}

export function updateTaskNotes(state, loc, id, notes) {
  const series = seriesOf(state, id);
  if (series && loc?.zone === 'day') {
    if ((series.notes || '') === notes) return state;
    return withSeries(state, id, { notes });
  }
  const current = listFrom(state, loc);
  const existing = current.find((task) => task.id === id);
  if (!existing || (existing.notes || '') === notes) return state;
  return withList(
    state,
    loc,
    current.map((task) => (task.id === id ? { ...task, notes } : task))
  );
}

export function toggleTaskDone(state, loc, id, { completedToEnd = false } = {}) {
  const series = seriesOf(state, id);
  if (series && loc?.zone === 'day' && loc.date) {
    const doneOn = new Set(series.doneOn || []);
    if (doneOn.has(loc.date)) doneOn.delete(loc.date);
    else doneOn.add(loc.date);
    return withSeries(state, id, { doneOn: [...doneOn].sort() });
  }
  const current = listFrom(state, loc);
  const index = current.findIndex((task) => task.id === id);
  if (index < 0) return state;
  const task = { ...current[index], done: !current[index].done };

  if (!completedToEnd) {
    return withList(
      state,
      loc,
      current.map((item, i) => (i === index ? task : item))
    );
  }

  const rest = current.filter((_, i) => i !== index);
  if (task.done) return withList(state, loc, [...rest, task]);
  let lastOpen = -1;
  rest.forEach((item, i) => {
    if (!item.done) lastOpen = i;
  });
  rest.splice(lastOpen + 1, 0, task);
  return withList(state, loc, rest);
}

export function deleteTaskById(state, loc, id) {
  if (seriesOf(state, id)) {
    return { ...state, recurring: state.recurring.filter((item) => item.id !== id) };
  }
  const current = listFrom(state, loc);
  if (!current.some((task) => task.id === id)) return state;
  return withList(
    state,
    loc,
    current.filter((task) => task.id !== id)
  );
}

export function moveTask(state, source, target) {
  if (!source || !target || !Number.isFinite(target.index)) return state;
  if (source.id && seriesOf(state, source.id)) return state;

  const from = listFrom(state, source);
  let sourceIndex = source.index;
  if (source.id) {
    const found = from.findIndex((task) => task.id === source.id);
    if (found >= 0) sourceIndex = found;
  }
  if (!Number.isFinite(sourceIndex) || sourceIndex < 0 || sourceIndex >= from.length) return state;

  const same =
    source.zone === target.zone &&
    source.date === target.date &&
    source.pageId === target.pageId &&
    source.listId === target.listId;

  if (same) {
    const next = from.slice();
    const [task] = next.splice(sourceIndex, 1);
    let index = target.index;
    if (index > sourceIndex) index -= 1;
    index = Math.max(0, Math.min(index, next.length));
    if (index === sourceIndex) return state;
    next.splice(index, 0, task);
    return withList(state, source, next);
  }

  const fromNext = from.slice();
  const [task] = fromNext.splice(sourceIndex, 1);
  const removed = withList(state, source, fromNext);
  const dest = listFrom(removed, target).slice();
  const index = Math.max(0, Math.min(target.index, dest.length));
  dest.splice(index, 0, task);
  return withList(removed, target, dest);
}

export function addPage(state, id) {
  const page = {
    id,
    name: 'Notes',
    lists: [{ id: createId(), title: 'New list', tasks: [] }],
  };
  return {
    ...state,
    pages: [...state.pages, page],
    activePageId: id,
  };
}

export function renamePage(state, id, name) {
  const next = name.trim();
  if (!next) return state;
  return {
    ...state,
    pages: state.pages.map((page) => (page.id === id ? { ...page, name: next } : page)),
  };
}

export function deletePage(state, id) {
  if (state.pages.length <= 1) return state;
  const pages = state.pages.filter((page) => page.id !== id);
  const activePageId = state.activePageId === id ? pages[0].id : state.activePageId;
  return { ...state, pages, activePageId };
}

export function addList(state, pageId, id) {
  return {
    ...state,
    pages: state.pages.map((page) =>
      page.id === pageId
        ? {
            ...page,
            lists: [...page.lists, { id, title: 'New list', tasks: [] }],
          }
        : page
    ),
  };
}

export function renameList(state, pageId, listId, title) {
  return {
    ...state,
    pages: state.pages.map((page) => {
      if (page.id !== pageId) return page;
      return {
        ...page,
        lists: page.lists.map((list) =>
          list.id === listId ? { ...list, title } : list
        ),
      };
    }),
  };
}

export function reorderLists(state, pageId, fromIndex, toIndex) {
  if (fromIndex === toIndex) return state;
  return {
    ...state,
    pages: state.pages.map((page) => {
      if (page.id !== pageId) return page;
      const lists = page.lists.slice();
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= lists.length ||
        toIndex >= lists.length
      ) {
        return page;
      }
      const [item] = lists.splice(fromIndex, 1);
      lists.splice(toIndex, 0, item);
      return { ...page, lists };
    }),
  };
}

export function deleteList(state, pageId, listId) {
  let changed = false;
  const pages = state.pages.map((page) => {
    if (page.id !== pageId) return page;
    if (!page.lists.some((list) => list.id === listId)) return page;
    changed = true;
    return { ...page, lists: page.lists.filter((list) => list.id !== listId) };
  });
  if (!changed) return state;
  return { ...state, pages };
}

export function reorderTask(state, loc, index, direction) {
  const list = listFrom(state, loc);
  const next = index + direction;
  if (next < 0 || next >= list.length) return state;
  const targetIndex = direction > 0 ? next + 1 : next;
  return moveTask(state, { ...loc, index }, { ...loc, index: targetIndex });
}

export function searchBoard(state, query) {
  const q = foldText(query.trim());
  if (!q) return [];
  const results = [];

  Object.keys(state.days)
    .sort()
    .forEach((date) => {
      state.days[date].forEach((task, index) => {
        if (foldText(task.text).includes(q)) {
          results.push({ kind: 'day', date, index, task });
        }
      });
    });

  (state.recurring || []).forEach((item) => {
    if (foldText(item.text).includes(q)) {
      results.push({ kind: 'recur', item, task: { id: item.id, text: item.text, done: false } });
    }
  });

  state.pages.forEach((page) => {
    page.lists.forEach((list) => {
      list.tasks.forEach((task, index) => {
        if (foldText(task.text).includes(q)) {
          results.push({
            kind: 'list',
            pageId: page.id,
            pageName: page.name,
            listId: list.id,
            listTitle: list.title,
            index,
            task,
          });
        }
      });
    });
  });

  return results;
}
